import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Form,
	FormInstance,
	Tag,
} from 'antd';
import { FuelTypes, PortActionTypes } from '@shared/utils/constants';
import {
	getUtcOffset,
	toMoment,
} from '@shared/utils/date';
import { formatOffset } from '@shared/utils/timezones';
import normalize from '@shared/utils/normalizeNumber';
import Button from '@client/components/Button';
import Select from '@client/components/Select';
import {
	ItineraryPortCallDto,
	NullableBunkerRecord,
} from '@client/screens/estimates/details/helpers/types';
import showErrorNotification from '@client/utils/showErrorNotification';
import {
	createRobs,
	updatePortCallAction,
	updateRob,
} from '@client/lib/api';
import DatePicker from '@client/components/DatePicker';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import RobTable from '@client/screens/fleet/VoyageDetailsScreen/tabs/components/RobTable';
import { BunkerRecord } from '@client/screens/fleet/VoyageDetailsScreen/tabs/components/RobEntryForm';
import { useItinerary } from '../ItineraryProvider';

const ActivityWithRobsForm = ({
	form,
	editingAction,
	afterOnSave,
}: {
	form: FormInstance;
	editingAction: ItineraryPortCallDto['actions'][number] | null;
	afterOnSave: () => void;
}) => {
	const editingRob = editingAction;

	const [robs, setRobs] = useState<NullableBunkerRecord[]>([]);
	const initialRobsRef = useRef<NullableBunkerRecord[]>([]);

	useEffect(() => {
		if (editingRob) {
			const initial: NullableBunkerRecord[] = editingRob.RobBunkers.flatMap((rb) => {
				const rows = [];

				const totalQuantity = normalize(rb.totalQuantity);
				const forecastedQuantity = normalize(rb.forecastedQuantity);

				if (totalQuantity.toFixed(3) != forecastedQuantity.toFixed(3)) {
					rows.push({
						id: rb.id,
						fuelGrade: rb.fuelGrade,
						quantity: totalQuantity,
						kind: 'act' as const,
					});
				} else {
					rows.push({
						id: rb.id,
						fuelGrade: rb.fuelGrade,
						quantity: forecastedQuantity,
						kind: 'est' as const,
					});
				}

				return rows;
			});

			initialRobsRef.current = initial;
			setRobs(initial);
		} else {
			initialRobsRef.current = [];
			setRobs([]);
		}
	}, [editingRob?.id]);

	const normalizeRobs = (r: NullableBunkerRecord[]) =>
		r
			.filter((e): e is BunkerRecord => e.quantity !== null)
			.map(({ fuelGrade, quantity }) => ({ fuelGrade, quantity }))
			.sort((a, b) => a.fuelGrade.localeCompare(b.fuelGrade));

	const hasRobChanges = () => {
		return (
			JSON.stringify(normalizeRobs(robs)) !==
			JSON.stringify(normalizeRobs(initialRobsRef.current))
		);
	};

	const robTouched = hasRobChanges();

	const {
		expandedEntry,
		refreshItinerary,
		setItineraryShouldUpdate,
	} = useItinerary();

	useEffect(() => {
		if (editingAction) {
			form.setFieldsValue(editingAction);
		} else {
			form.resetFields();
			setRobs([]);
		}
	}, [form, editingAction]);

	const {
		cargos,
		voyageDetails,
		fixtureCurrency,
		vessel,
	} = useVoyage();

	const selectedPortCall = expandedEntry as ItineraryPortCallDto;
	const isEdit = !!editingAction;

	const [displayOffMin, setDisplayOffMin] = useState<number>(0);

	useEffect(() => {
		if (!editingAction) {
			form.resetFields();
			setRobs([]);
			setDisplayOffMin(0);

			return;
		}

		const storedUtc = editingAction.date ? toMoment(editingAction.date) : null;

		const offMin = selectedPortCall ?
			(getUtcOffset(selectedPortCall.port, storedUtc?.toDate()) ?? 0) : 0;

		const portLocal = storedUtc?.clone().utcOffset(offMin);

		form.setFieldsValue({
			date: portLocal,
			event: editingAction.event,
			cargoId: editingAction.cargoId,
		});
		setDisplayOffMin(offMin);
	}, [editingAction?.id, expandedEntry, form]);

	const onSave = async () => {
		const {
			event,
			date,
			cargoId,
		} = form.getFieldsValue(true);

		if (expandedEntry == null || vessel == null) {
			return;
		}

		const localMoment = toMoment(date).clone();
		const offMin = getUtcOffset(
			selectedPortCall.port,
			localMoment.toDate(),
		) ?? 0;

		const dateToStore = localMoment.clone().utcOffset(offMin, true);

		try {
			const filteredEntries = robs.filter(
				(entry): entry is BunkerRecord => entry.quantity !== null,
			);

			if (isEdit) {
				await updatePortCallAction(
					expandedEntry.vesselId,
					expandedEntry.id,
					editingAction.id,
					event,
					dateToStore,
					cargoId,
				);

				if (
					robTouched && (
						editingRob?.event === PortActionTypes.ARRIVAL ||
						editingRob?.event === PortActionTypes.DEPARTURE
					)
				) {
					const attributes = {
						currency: fixtureCurrency,
						remainingOnBoard:
							filteredEntries.length > 1 ?
								(filteredEntries as BunkerRecord[]) :
								(filteredEntries[0] as BunkerRecord),
					};

					if (editingRob != null) {
						await updateRob({
							portCallActionId: editingRob.id,
							vesselId: vessel.id,
							voyageId: voyageDetails.id,
							attributes,
							currency: fixtureCurrency,
							date: selectedDate ?? undefined,
							port: form.getFieldValue('port'),
							event: editingRob.event,
							deleteMissingEntries: true,
							// @ts-ignore
							resetQueue: editingRob.event === PortActionTypes.COMMENCEMENT,
						});
					} else {
						await createRobs({
							vesselId: vessel.id,
							voyageId: voyageDetails.id,
							event,
							port: selectedPortCall.port,
							date: dateToStore,
							currency: fixtureCurrency,
							robs: filteredEntries,
							bunkersReceived: [],
							cargoId,
							portCallId: selectedPortCall.id,
						});
					}
				}
			} else {
				await createRobs({
					vesselId: vessel.id,
					voyageId: voyageDetails.id,
					event,
					port: selectedPortCall.port,
					date: dateToStore,
					currency: fixtureCurrency,
					robs: filteredEntries,
					bunkersReceived: [],
					portCallId: selectedPortCall.id,
					cargoId,
				});
			}

			await refreshItinerary();
			setItineraryShouldUpdate(true);
			afterOnSave();
		} catch (e) {
			showErrorNotification('Could not create action', e as Error);
		} finally {
			form.resetFields();
		}
	};

	const deleteEntry = (record: NullableBunkerRecord): void => {
		if (record == null) {
			return;
		}

		const index = robs.findIndex(
			(entry) => entry.fuelGrade === record.fuelGrade &&
				entry.quantity === record.quantity &&
				entry.pricePerTon === record.pricePerTon,
		);

		if (index !== -1) {
			const newArr = [...robs];
			newArr.splice(index, 1);
			setRobs(newArr);
		}
	};

	const fuelOptions = Object.keys(FuelTypes).map((key) => ({
		label: FuelTypes[key as keyof typeof FuelTypes],
		value: key,
	}));

	const selectedDate = useMemo(
		() => form.getFieldValue('date') ?? editingAction?.date,
		[form.getFieldsValue(['date']), editingAction]
	);

	const getDateTag = () => {
		if (!editingAction || !selectedPortCall) {
			return null;
		}

		const event = editingAction.event;

		const isActual =
			(event === PortActionTypes.ARRIVAL && selectedPortCall.arrivalDate != null) ||
			(event === PortActionTypes.DEPARTURE && selectedPortCall.departureDate != null);

		return {
			label: isActual ? 'Actual' : 'Estimated',
			color: isActual ? 'darkgrey' : 'blue',
		};
	};

	const dateTag = () => {
		const meta = getDateTag();

		if (!meta) {
			return undefined;
		}

		return <Tag color={meta.color}>{meta.label}</Tag>;
	};

	return (
		<>
			<Form.Item required label="Date" name="date">
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<DatePicker
						time
						value={selectedDate}
						onChange={(val) => {
							if (!val || Array.isArray(val)) {
								return;
							}

							form.setFieldsValue({ date: val });

							if (selectedPortCall) {
								setDisplayOffMin(
									getUtcOffset(
										selectedPortCall.port,
										val,
									) ?? 0,
								);
							}
						}}
						showTimezone={false}
					/>
					<span style={{ opacity: 0.7 }}>{formatOffset(displayOffMin)}</span>
					{dateTag()}
				</div>
			</Form.Item>
			<Form.Item label="Cargo (optional)" name="cargoId">
				<Select
					showSearch
					placeholder="Select cargo"
					options={(cargos ?? []).map((c) => ({
						label: c.type,
						value: c.id,
					}))}
				/>
			</Form.Item>
			{(editingAction?.event === PortActionTypes.ARRIVAL || editingAction?.event === PortActionTypes.DEPARTURE) && (
				<RobTable
					label="ROB"
					dataSource={robs}
					onChange={setRobs}
					onDelete={deleteEntry}
					fixtureCurrency={fixtureCurrency}
					fuelOptions={fuelOptions}
					showPricePerTon={false}
				/>
			)}

			<Button onClick={onSave} type="primary">
				{isEdit ? 'Update activity' : 'Create Activity'}
			</Button>
		</>
	);
};

export default ActivityWithRobsForm;
